import { Link } from "react-router-dom";

import { AccessStamp } from "./AccessStamp";
import { PlaybookArt } from "./PlaybookArt";
import type { Playbook } from "../lib/types";

/**
 * One playbook, as a tile.
 *
 * The start page draws one per playbook, and a tile starts an engagement: it
 * goes to that playbook's own form. There was a Playbooks page that drew the
 * same tiles a second time to explain each one; it was removed, and the
 * start page is the one place a playbook is chosen.
 */
export function PlaybookTile({
  playbook,
  to,
  cost,
}: {
  playbook: Playbook;
  to: string;
  /** What it usually costs and takes, or its ceiling — the start page's
   * answer to "how big a commitment is this" (#124). */
  cost?: React.ReactNode;
}) {
  const body = (
    <>
      <div className="mb-4 rounded-sm bg-surface-2 px-4 py-3 transition-colors group-hover:bg-surface">
        <PlaybookArt
          playbook={playbook.key}
          owned={playbook.owner_id !== null}
          className="tile-art"
        />
      </div>
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-sm font-medium text-ink">{playbook.title}</h2>
        <AccessStamp access={playbook.access} className="mt-0.5" />
      </div>
      <p className="mt-1.5 flex-1 text-xs leading-relaxed text-ink-2">
        {playbook.description}
      </p>
      {/* Who it is for, as a line rather than a row of pills: three
          outlined shapes per tile said what one sentence does. */}
      {playbook.audience.length > 0 && (
        <p className="mt-3 text-xs text-muted">For {playbook.audience.join(" · ")}</p>
      )}
      {/* The deliverable's filename was here (REPORT.MD), which tells a
          reader nothing they choose by; what it costs does (#124). */}
      <p className="mt-4 text-xs text-muted">
        {cost && <>{cost} · </>}
        <span className="font-mono tabular-nums">{playbook.workstreams.length}</span>{" "}
        {playbook.workstreams.length === 1 ? "workstream" : "workstreams"}
      </p>
    </>
  );

  return (
    <Link
      to={to}
      className="tile group flex flex-col rounded-lg border border-line bg-surface p-5 text-left transition-colors hover:border-line-strong hover:bg-surface-2"
    >
      {body}
    </Link>
  );
}
